/**
 * The activity bar's Huddles view (core HuddlesViewModel): "New huddle" (Start / Get a link / Schedule), All huddles,
 * then Live now, Upcoming and My links. Works with Chat off.
 */
import { Shell, type OpenTarget } from "@bundle/core";
import { huddleTitle, IconButton, Menu, SidebarNavRow, Text, todayIso, useFollow, useFormat, useHuddlesArea, useT, useWorkspace, type MenuEntry } from "@bundle/ui";
import { useEffect } from "react";

/** Rows each section shows before "All huddles". */
const SECTION_ROWS = 5;

export function SidebarHuddles({ activeUrl, open }: { activeUrl: string; open: (url: string, target?: OpenTarget) => void }) {
  const t = useT();
  const fmt = useFormat();
  const ws = useWorkspace();
  const area = useHuddlesArea();
  const follow = useFollow(open);
  useEffect(() => {
    void area?.vm.refresh();
  }, [area?.vm]);
  if (area === null) return null;
  const { vm, state } = area;
  const cache = ws.state.cache;
  const all = Shell.formatUrl({ type: "huddles", ws: ws.slug });
  const active = Shell.parseUrl(activeUrl);
  const menu: MenuEntry[] = [{ id: "start", label: t("huddles.start"), icon: "huddle", onSelect: () => vm.openStart() }];
  if (state.linksOn) menu.push({ id: "link", label: t("huddles.getLink"), icon: "link", onSelect: () => vm.openLink("link", todayIso(), t("huddles.untitled")) });
  menu.push({ id: "schedule", label: t("huddles.schedule"), icon: "calendar", onSelect: () => vm.openLink("schedule", todayIso(), t("huddles.untitled")) });
  const { live, upcoming, myLinks } = state.sections;
  const inCall = (key: string) => active?.type === "huddle" && active.huddleId === key;
  return (
    <>
      <div className="sidebar-view-header">
        <Text variant="headline" weight="semibold">
          {t("shell.view.huddles")}
        </Text>
        <Menu items={menu} align="end" testID="sidebar-huddles-menu">
          <IconButton icon="plus" size="sm" accessibilityLabel={t("huddles.new")} onPress={() => undefined} testID="sidebar-huddles-new" />
        </Menu>
      </div>
      <div className="sidebar-group">
        <SidebarNavRow icon="huddle" label={t("huddles.all")} selected={active?.type === "huddles"} onPress={() => open(all)} testID="nav-huddles" />
      </div>
      {live.length === 0 ? null : (
        <div className="sidebar-group" data-testid="sidebar-huddles-live">
          <div className="sidebar-view-label">
            <Text variant="caption" weight="semibold" color="text.tertiary">
              {t("huddles.section.live")}
            </Text>
          </div>
          {live.slice(0, SECTION_ROWS).map((h) => (
            <SidebarNavRow
              key={h.id}
              icon="huddle"
              label={huddleTitle(cache, h.channelId, h.meeting?.title, t)}
              badge={h.participants.length}
              selected={inCall(h.channelId)}
              onPress={() => follow(vm.join(h.channelId))}
              testID={`sidebar-huddle-live-${h.id}`}
            />
          ))}
        </div>
      )}
      {upcoming.length === 0 ? null : (
        <div className="sidebar-group" data-testid="sidebar-huddles-upcoming">
          <div className="sidebar-view-label">
            <Text variant="caption" weight="semibold" color="text.tertiary">
              {t("huddles.section.upcoming")}
            </Text>
          </div>
          {upcoming.slice(0, SECTION_ROWS).map((m) => (
            <SidebarNavRow
              key={m.code}
              icon="calendar"
              label={`${m.title === undefined || m.title === "" ? t("huddles.untitled") : m.title} · ${fmt.time(m.scheduledAt!)}`}
              selected={false}
              onPress={() => open(all)}
              testID={`sidebar-huddle-upcoming-${m.code}`}
            />
          ))}
        </div>
      )}
      {myLinks.length === 0 ? null : (
        <div className="sidebar-group" data-testid="sidebar-huddles-links">
          <div className="sidebar-view-label">
            <Text variant="caption" weight="semibold" color="text.tertiary">
              {t("huddles.section.links")}
            </Text>
          </div>
          {myLinks.slice(0, SECTION_ROWS).map((m) => (
            <SidebarNavRow
              key={m.code}
              icon="link"
              label={m.title === undefined || m.title === "" ? m.code : m.title}
              selected={inCall(m.channelId ?? `meeting:${m.code}`)}
              onPress={() => follow(vm.joinLink(m))}
              testID={`sidebar-huddle-link-${m.code}`}
            />
          ))}
        </div>
      )}
    </>
  );
}
